import { Box, Tooltip } from "@mui/joy";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { useAuth, useRoles } from "../../../contexts/api";
import { Permission, usePermissions } from "../../../contexts/app/permissions";
import { SUserAdd } from "../../../styles/StyledMUI";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import RoleCard from "./display/RoleCard";
import RoleCreateModal from "./display/RoleCreateModal";
import RoleEditModal from "./display/RoleEditModal";
import type { Role } from "../../../api/roles/Role.typed";
import { useRolesPageModalsState } from "./hooks/useRolesPageModalsState";

export default function RolesPage() {
  const { user } = useAuth();
  const { permissionsCatalog, getRole, createRole, updateRole, deleteRole } = useRoles();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.USERS_ROLES_EDIT);
  const confirm = useConfirmationModal();

  const {
    isOpen,
    setIsOpen,
    newRoleName,
    setNewRoleName,
    newRoleEmployee,
    setNewRoleEmployee,
    selectedPerms,
    setSelectedPerms,
    isEditOpen,
    setIsEditOpen,
    editRoleId,
    setEditRoleId,
    editRoleName,
    setEditRoleName,
    editRoleEmployee,
    setEditRoleEmployee,
    editSelectedPerms,
    setEditSelectedPerms,
    resetCreateModal,
    resetEditModal,
  } = useRolesPageModalsState();

  const roles = permissionsCatalog?.roles || [];
  const allPermissions = permissionsCatalog?.allPermissions || [];
  const permsLoading = !permissionsCatalog;

  const handleEditRole = async (role: Role) => {
    setEditRoleId(role.id);
    setEditRoleName(role.name);
    setEditRoleEmployee(!!role.is_employee);
    setEditSelectedPerms([...(role.permission_list || [])]);
    setIsEditOpen(true);
    try {
      const fullRole = await getRole(role.id);
      const validPerms = new Set(allPermissions);
      const perms = (fullRole.permission_list || []).filter((p) => validPerms.has(p));
      setEditRoleName(fullRole.name);
      setEditRoleEmployee(!!fullRole.is_employee);
      setEditSelectedPerms(perms);
    } catch {
      // Garder les données du catalogue si l’appel échoue
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    const ok = await confirm.showConfirmation("Confirmation", "Supprimer ce rôle ?");
    if (ok) deleteRole(roleId);
  };

  const currentCompanyId = user?.Role?.company_id ?? user?.company_id ?? 0;

  const handleCreateSubmit = () => {
    if (!newRoleName.trim()) return;
    createRole({
      name: newRoleName.trim(),
      permission_list: selectedPerms,
      company_id: currentCompanyId,
      is_employee: newRoleEmployee,
    }).then(() => {
      resetCreateModal();
    });
  };

  const handleEditSubmit = async () => {
    if (!editRoleId) return;
    const ok = await confirm.showConfirmation("Confirmation", "Mettre à jour ce rôle ?");
    if (!ok) return;
    await updateRole(editRoleId, {
      name: editRoleName.trim(),
      permission_list: editSelectedPerms,
      is_employee: editRoleEmployee,
    });
    resetEditModal();
  };

  const handleCreateClose = () => {
    resetCreateModal();
  };

  const handleEditClose = () => {
    resetEditModal();
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "calc(100vh - 120px)",
        p: 2,
        display: "grid",
        gap: 3,
        overflow: "auto",
      }}
    >
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Tooltip title={!canEdit ? "Droits d'édition requis" : "Nouveau rôle"}>
            <span>
              <IconButtonCTA
                ctaVariant="accent"
                aria-label="Nouveau rôle"
                onClick={() => setIsOpen(true)}
                disabled={!canEdit}
              >
                <SUserAdd />
              </IconButtonCTA>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ mt: 1, display: "grid", gap: 2 }}>
          {roles.length === 0 && (
            <DarkTypography level="body-md">Aucun rôle disponible.</DarkTypography>
          )}
          {roles.map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              canEdit={canEdit}
              onEdit={handleEditRole}
              onDelete={handleDeleteRole}
            />
          ))}
        </Box>
      </Box>

      <RoleCreateModal
        open={isOpen}
        onClose={handleCreateClose}
        roleName={newRoleName}
        onRoleNameChange={setNewRoleName}
        isEmployee={newRoleEmployee}
        onIsEmployeeChange={setNewRoleEmployee}
        selectedPerms={selectedPerms}
        onSelectedPermsChange={setSelectedPerms}
        allPermissions={allPermissions}
        permsLoading={permsLoading}
        onSubmit={handleCreateSubmit}
        canEdit={canEdit}
      />

      <RoleEditModal
        open={isEditOpen}
        onClose={handleEditClose}
        roleName={editRoleName}
        onRoleNameChange={setEditRoleName}
        isEmployee={editRoleEmployee}
        onIsEmployeeChange={setEditRoleEmployee}
        selectedPerms={editSelectedPerms}
        onSelectedPermsChange={setEditSelectedPerms}
        allPermissions={allPermissions}
        permsLoading={permsLoading}
        onSubmit={handleEditSubmit}
        canEdit={canEdit}
      />
    </Box>
  );
}
